/**
 * Mobile buy bar — reserves room at the end of the page for the pinned PDP
 * add-to-cart row.
 *
 * The row itself is pinned in CSS (the `position: fixed` block in
 * sections/product-information.liquid, which documents why it is not sticky). Fixed,
 * it can no longer reserve its own room, so this publishes its height as
 * `--mobile-buy-bar-height` on `:root` and the footer pads its trailing edge by that
 * much, keeping its last row out from under the bar.
 *
 * A measurement, not a scroll effect: the bar's position never changes, so nothing
 * here runs on the scroll path. Mobile only, and a no-op with no bar on the page —
 * the CSS falls back to 0px, i.e. a bar overlapping the footer's last row.
 */

const MOBILE = '(max-width: 767px)';

/* Kept in step with the CSS rule this feeds — including the gift-card opt-out, so a
   gift card's recipient form (which stays in the flow) reserves nothing. */
const BAR = 'main[data-template^="product"] .product-details .buy-buttons-block:not(:has(.buy-buttons-block__bar))';

const HEIGHT = '--mobile-buy-bar-height';

const root = document.documentElement;
const mobile = matchMedia(MOBILE);

/** @type {ResizeObserver | null} */
let observer = null;

/**
 * Starts measuring, if there is anything to measure.
 * @returns {ResizeObserver | null}
 */
function bind() {
  const bar = document.querySelector(BAR);
  if (!(bar instanceof HTMLElement)) return null;

  // Fires once on observe, and again whenever the bar's own height changes — a
  // sold-out variant's label, a longer translation, a wrapped button.
  const resizeObserver = new ResizeObserver(() => root.style.setProperty(HEIGHT, `${bar.offsetHeight}px`));
  resizeObserver.observe(bar);

  return resizeObserver;
}

function rebind() {
  observer?.disconnect();
  observer = null;

  if (mobile.matches) {
    observer = bind();
  } else {
    root.style.removeProperty(HEIGHT);
  }
}

mobile.addEventListener('change', rebind);

// The theme editor re-renders the whole section on a setting change, replacing the
// bar this is bound to (there is no event for a block-level re-render, but those come
// with a section render on the settings that matter here).
document.addEventListener('shopify:section:load', rebind);

rebind();
